import React from 'react'
import { TreeNode } from '@/components/molecules/TreeNode/TreeNode'
import { NestedCheckboxGroupOptionType } from '@/components'
import { DataSetSelector } from '@/components/molecules'
import { getStrokeHexValue } from '@/components/organisms/MultiChart/getStrokeHexValue'
import { useChartColorStore } from '@/stores/useChartColorStore/useChartColorStore'

export type DataSetTreeNodeGroupProps = {
    options: Array<NestedCheckboxGroupOptionType>
    checkedItems: string[]
    onChange: (newCheckedItems: string[]) => void
    isRoot?: boolean
}

export const DataSetTreeNodeGroup: React.FC<DataSetTreeNodeGroupProps> = ({
    options,
    checkedItems,
    onChange,
    isRoot = true,
}) => {
    const updateColors = useChartColorStore((state) => state.updateColors)

    const colorIndices = React.useMemo(() => {
        return updateColors(checkedItems)
    }, [checkedItems, updateColors])

    const handleChange = (value: string, isChecked: boolean) => {
        // Add or remove the value from the checkedItems array
        const newCheckedItems = checkedItems.slice()

        if (isChecked) {
            newCheckedItems.push(value)
            onChange(newCheckedItems)
            return
        }

        const index = newCheckedItems.indexOf(value)

        if (index > -1) {
            newCheckedItems.splice(index, 1)
            onChange(newCheckedItems)
            return
        }

        onChange(newCheckedItems)
    }

    return (
        <div className="space-y-2">
            {options.map((option) => {
                const optionColor = checkedItems.includes(option.value)
                    ? getStrokeHexValue(colorIndices[option.value])
                    : undefined

                const countCheckedChildren = (
                    children: NestedCheckboxGroupOptionType[]
                ): number => {
                    return children.reduce((count, child) => {
                        if (checkedItems.includes(child.value)) {
                            count++
                        }
                        if (child.children) {
                            count += countCheckedChildren(child.children)
                        }
                        return count
                    }, 0)
                }

                const selectedChildrenCount = option.children
                    ? countCheckedChildren(option.children)
                    : 0

                return (
                    <TreeNode
                        key={option.value}
                        label={option}
                        renderLabel={(option, isOpen) => {
                            const labelText =
                                typeof option.label === 'string'
                                    ? option.label
                                    : ''
                            return (
                                <DataSetSelector
                                    label={labelText}
                                    isSelected={checkedItems.includes(
                                        option.value
                                    )}
                                    onChange={(isSelected) => {
                                        handleChange(option.value, isSelected)
                                    }}
                                    color={optionColor}
                                    selectedChildrenCount={
                                        !isOpen && selectedChildrenCount > 0
                                            ? selectedChildrenCount
                                            : undefined
                                    }
                                />
                            )
                        }}
                        isRoot={isRoot}
                    >
                        {option.children && (
                            <DataSetTreeNodeGroup
                                options={option.children}
                                checkedItems={checkedItems}
                                onChange={onChange}
                                isRoot={false}
                            />
                        )}
                    </TreeNode>
                )
            })}
        </div>
    )
}
